import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { resolveInvoiceFollowupContext } from "../../utilis/followupConstants";
import Swal from "sweetalert2";
import { getApiErrorMessage } from "../../utilis/apiErrorMessage";
import {
  applyInvoiceCatalog,
  fetchApplicationInvoiceDetail,
  fetchInvoiceNoteList,
  workspaceErrorMessage,
} from "../../utilis/invoiceWorkspaceApi";
import InvoiceCreateWizard from "./InvoiceCreateWizard";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import {
  buildFinanceInvoicesReturnUrl,
  readFinanceInvoiceListTabsFromLocation,
  buildInvoiceCreateRequestBody,
  getFinanceInvoiceApiMessage,
  isFinanceInvoiceApiSuccess,
  mapFinanceInvoicePayloadTaxTypesForApi,
  normalizeFinanceInvoiceList,
} from "../../utilis/invoiceCreateHelpers";
import { normalizeClientAllApplications } from "../../utilis/invoiceClientApplicationHelpers";
import {
  ensureFinanceInvoiceApprovedState,
  normalizeFinanceInvoiceFromRecord,
  normalizeFinanceInvoiceRecord,
  normalizeInvoiceNotesList,
  applyFinanceInvoiceMutationFromResponse,
  applyInvoiceNotesMutationFromResponse,
} from "../../utilis/invoiceEditHelpers";
import {
  buildFinanceInvoicePaymentFormData,
  buildFinanceInvoiceReversePaymentPayload,
  buildMultipartFormDataRequestConfig,
  getFinanceInvoicePaymentApiMessage,
  isFinanceInvoicePaymentApiSuccess,
  recordFinanceInvoicePaymentsSequentially,
} from "../../utilis/financeInvoicePaymentHelpers";
import { getInvoiceStatusLabel, normalizeInvoiceStatus } from "../../utilis/invoiceStatusHelpers";
import {
  applyCommissionApproveFields,
  sumCommissionPayloadPaidAmount,
} from "../reports/commissions/commissionInvoiceApproveHelpers";
import {
  showCommissionInvoiceSuccessToast,
} from "../reports/commissions/commissionInvoiceToast";
import { mapCommissionPaymentsFromPayload } from "../reports/commissions/commissionInvoicePaymentHelpers";
import {
  moveFinanceInvoiceToDraft,
  voidFinanceInvoice,
} from "../reports/commissions/commissionsApi";
import { canViewApplicationInvoices } from "../../utilis/invoicePermissionHelpers";
import "../Clients/clients.css";
import "../reports/commissions/commissions-responsive.css";

function FinanceEditInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, invoiceId } = useParams();
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [invoiceSetting, setInvoiceSetting] = useState(null);
  const [discountFeeRules, setDiscountFeeRules] = useState([]);
  const [existingInvoices, setExistingInvoices] = useState([]);
  const [clientApplications, setClientApplications] = useState([]);
  const [existingInvoice, setExistingInvoice] = useState(null);
  const [invoiceNotes, setInvoiceNotes] = useState([]);
  const [loadingInvoiceNotes, setLoadingInvoiceNotes] = useState(false);
  const [addingInvoiceNote, setAddingInvoiceNote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingFinancePayment, setSavingFinancePayment] = useState(false);
  const [reversingFinancePayment, setReversingFinancePayment] = useState(false);
  const [voidingInvoice, setVoidingInvoice] = useState(false);
  const [movingInvoiceToDraft, setMovingInvoiceToDraft] = useState(false);
  const [followupUsers, setFollowupUsers] = useState([]);
  const [followupRefreshKey, setFollowupRefreshKey] = useState(0);
  const [followupCounts, setFollowupCounts] = useState({ pending: 0, total: 0 });
  const [invoiceCatalog, setInvoiceCatalog] = useState(null);
  const clientId = useMemo(() => String(id || "").trim(), [id]);
  const resolvedInvoiceId = useMemo(
    () => String(invoiceId || "").trim(),
    [invoiceId],
  );

  const invoiceFollowupConfig = useMemo(() => {
    if (!resolvedInvoiceId) return null;
    return {
      financeInvoiceId: Number(resolvedInvoiceId),
      contextType: resolveInvoiceFollowupContext(existingInvoice),
      clientId: clientId ? Number(clientId) : null,
      users: followupUsers,
      pendingCount: followupCounts.pending,
      totalCount: followupCounts.total,
      refreshKey: followupRefreshKey,
      onFollowupAdded: () => setFollowupRefreshKey((k) => k + 1),
    };
  }, [
    resolvedInvoiceId,
    existingInvoice,
    clientId,
    followupUsers,
    followupCounts,
    followupRefreshKey,
  ]);

  const commissionShellStatus = useMemo(() => {
    if (existingInvoice?.status) {
      const normalized = normalizeInvoiceStatus(existingInvoice.status);
      if (normalized === "draft" || normalized === "awaiting_approval") {
        return normalized;
      }
    }
    if (existingInvoice?.approved) return "approved";
    if (existingInvoice?.status) {
      return normalizeInvoiceStatus(existingInvoice.status);
    }
    return "draft";
  }, [existingInvoice?.approved, existingInvoice?.status]);

  const isDraftLikeFinanceInvoice = useMemo(
    () =>
      commissionShellStatus === "draft" ||
      commissionShellStatus === "awaiting_approval",
    [commissionShellStatus],
  );

  const handleCatalogLoaded = useCallback((catalog) => {
    const mapped = applyInvoiceCatalog(catalog || {});
    setInvoiceCatalog(catalog || null);
    if (mapped.setting) setInvoiceSetting(mapped.setting);
    setDiscountFeeRules(mapped.discountRules);
    setFollowupUsers(mapped.followupUsers);
  }, []);

  const financeListReturnTabs = useMemo(
    () => readFinanceInvoiceListTabsFromLocation(location),
    [location],
  );

  const navigateToFinanceInvoiceList = useCallback(
    (statusTab) => {
      navigate(
        buildFinanceInvoicesReturnUrl({
          statusTab,
          moduleTab: financeListReturnTabs.moduleTab,
        }),
      );
    },
    [navigate, financeListReturnTabs.moduleTab],
  );

  const handleSaveComplete = useCallback(() => {
    navigateToFinanceInvoiceList("draft");
  }, [navigateToFinanceInvoiceList]);

  const handleVoidInvoice = async () => {
    const targetInvoiceId = existingInvoice?.id || resolvedInvoiceId;
    if (!targetInvoiceId) return false;

    const invoiceNumber =
      existingInvoice?.invoice_number ||
      existingInvoice?.invoice_no ||
      targetInvoiceId;

    const confirmed = await Swal.fire({
      title: "Void invoice?",
      text: `Move invoice ${invoiceNumber} to Void? The record will be kept for reference.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Void",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#64748b",
    });

    if (!confirmed.isConfirmed) return false;

    try {
      setVoidingInvoice(true);
      await voidFinanceInvoice(targetInvoiceId);
      await Swal.fire({
        icon: "success",
        title: "Voided",
        text: "Invoice moved to Void.",
        timer: 1400,
        showConfirmButton: false,
      });
      navigateToFinanceInvoiceList("void");
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to void invoice.",
        "error",
      );
      return false;
    } finally {
      setVoidingInvoice(false);
    }
  };

  const refreshExistingInvoiceRecord = useCallback(
    async (targetInvoiceId = resolvedInvoiceId) => {
      if (!targetInvoiceId) return null;
      try {
        const invoiceResponse = await apiUrl.get(
          `clientFinanceInvoices/${targetInvoiceId}`,
        );
        if (!isFinanceInvoiceApiSuccess(invoiceResponse)) {
          return null;
        }
        const mapped = ensureFinanceInvoiceApprovedState(
          normalizeFinanceInvoiceRecord(invoiceResponse),
        );
        if (mapped) {
          setExistingInvoice(mapped);
          return mapped;
        }
      } catch (error) {
        console.error("Failed to refresh finance invoice", error);
      }
      return null;
    },
    [resolvedInvoiceId],
  );

  const handleMoveInvoiceToDraft = async () => {
    const targetInvoiceId = existingInvoice?.id || resolvedInvoiceId;
    if (!targetInvoiceId) return false;

    const invoiceNumber =
      existingInvoice?.invoice_number ||
      existingInvoice?.invoice_no ||
      targetInvoiceId;

    const confirmed = await Swal.fire({
      title: "Move to draft?",
      text: `Invoice ${invoiceNumber} will return to Draft so you can edit and approve again.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Move to draft",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#64748b",
    });

    if (!confirmed.isConfirmed) return false;

    try {
      setMovingInvoiceToDraft(true);
      await moveFinanceInvoiceToDraft(targetInvoiceId);
      await Swal.fire({
        icon: "success",
        title: "Moved to draft",
        text: "Invoice is now in Draft.",
        timer: 1400,
        showConfirmButton: false,
      });
      navigateToFinanceInvoiceList("draft");
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to move invoice to draft.",
        "error",
      );
      return false;
    } finally {
      setMovingInvoiceToDraft(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const loadData = async () => {
      if (!clientId || !resolvedInvoiceId) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const workspace = await fetchApplicationInvoiceDetail({
          invoiceId: resolvedInvoiceId,
          clientId,
          signal: controller.signal,
        });

        if (cancelled) return;

        setClientData(workspace.client || null);
        setExistingInvoices(
          Array.isArray(workspace.sibling_invoice_numbers)
            ? workspace.sibling_invoice_numbers
            : [],
        );
        setClientApplications(
          Array.isArray(workspace.applications) ? workspace.applications : [],
        );
        setFollowupCounts(
          workspace.followup_counts || { pending: 0, total: 0 },
        );
        setInvoiceNotes([]);

        const invoiceResponse = {
          data: { success: true, data: workspace.invoice },
        };
        if (workspace.invoice) {
          setExistingInvoice(normalizeFinanceInvoiceRecord(invoiceResponse));
        } else {
          setExistingInvoice(null);
          Swal.fire("Error", "Failed to load invoice details.", "error");
        }
      } catch (error) {
        if (cancelled) return;

        const detail = workspaceErrorMessage(
          error,
          "Failed to load invoice details.",
        );
        if (!detail) return;

        console.error("Failed to load finance invoice edit page", error);
        Swal.fire("Error", detail, "error");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [clientId, resolvedInvoiceId]);

  const handleClose = () => {
    navigateToFinanceInvoiceList(financeListReturnTabs.statusTab);
  };

  const refreshInvoice = useCallback(
    async (targetInvoiceId = resolvedInvoiceId) => {
      if (!targetInvoiceId) return null;

      try {
        const response = await apiUrl.get(
          `clientFinanceInvoices/${targetInvoiceId}`,
        );
        if (isFinanceInvoiceApiSuccess(response)) {
          const invoice = normalizeFinanceInvoiceRecord(response);
          const refreshed = invoice
            ? {
                ...invoice,
                approved: Boolean(
                  invoice?.approved ?? existingInvoice?.approved,
                ),
              }
            : null;
          setExistingInvoice(refreshed);
          return refreshed;
        }
      } catch (error) {
        console.error("Failed to refresh invoice", error);
      }

      return null;
    },
    [resolvedInvoiceId, existingInvoice?.approved],
  );

  const handleAfterSaveAndAnother = useCallback(async () => {
    if (!resolvedInvoiceId) return;
    await refreshInvoice(resolvedInvoiceId);
  }, [resolvedInvoiceId, refreshInvoice]);

  const applyInvoiceMutationFromResponse = (response, targetInvoiceId) => {
    setExistingInvoice((prev) =>
      applyFinanceInvoiceMutationFromResponse(
        prev || normalizeFinanceInvoiceFromRecord({ id: targetInvoiceId }),
        response,
      ),
    );
    setInvoiceNotes((prev) => applyInvoiceNotesMutationFromResponse(prev, response));
  };

  const syncInvoiceAfterMutation = async (response, targetInvoiceId) => {
    applyInvoiceMutationFromResponse(response, targetInvoiceId);
    await Promise.all([
      refreshInvoice(targetInvoiceId),
      fetchInvoiceNotes(targetInvoiceId, { silent: true }),
    ]);
  };

  const handleAddFinanceInvoicePayment = async (
    payment,
    receiptFile = null,
    receiptFiles = null,
    options = {},
  ) => {
    const { silent = false, skipNavigate = false } = options;
    const targetInvoiceId = existingInvoice?.id || resolvedInvoiceId;
    if (!targetInvoiceId) {
      Swal.fire("Error", "Invoice details are missing.", "error");
      return false;
    }

    try {
      setSavingFinancePayment(true);
      const formData = buildFinanceInvoicePaymentFormData({
        invoiceId: targetInvoiceId,
        clientId,
        payment,
        receiptFile,
        receiptFiles,
      });

      const response = await apiUrl.post(
        `invoicepayment/${targetInvoiceId}`,
        formData,
        buildMultipartFormDataRequestConfig(),
      );

      if (!isFinanceInvoicePaymentApiSuccess(response)) {
        Swal.fire(
          "Error",
          getFinanceInvoicePaymentApiMessage(
            response,
            "Failed to record payment.",
          ),
          "error",
        );
        return false;
      }

      await syncInvoiceAfterMutation(response, targetInvoiceId);
      const refreshed = await refreshInvoice(targetInvoiceId);
      const refreshedStatus = normalizeInvoiceStatus(
        refreshed?.status || existingInvoice?.status,
      );

      if (refreshedStatus === "paid") {
        showCommissionInvoiceSuccessToast(
          getFinanceInvoicePaymentApiMessage(response, "Invoice paid in full."),
        );
      } else if (!silent) {
        showCommissionInvoiceSuccessToast(
          getFinanceInvoicePaymentApiMessage(
            response,
            "Payment recorded successfully.",
          ),
        );
      }

      if (!skipNavigate) {
        navigateToFinanceInvoiceList(financeListReturnTabs.statusTab);
      }
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to record payment.",
        "error",
      );
      return false;
    } finally {
      setSavingFinancePayment(false);
    }
  };

  const handleAddFinanceInvoiceBulkPayment = async (entries) => {
    const saved = await recordFinanceInvoicePaymentsSequentially(
      entries,
      (payment, receiptFile, receiptFiles, options = {}) =>
        handleAddFinanceInvoicePayment(payment, receiptFile, receiptFiles, {
          ...options,
          skipNavigate: true,
        }),
    );

    if (saved) {
      navigateToFinanceInvoiceList(financeListReturnTabs.statusTab);
    }

    return saved;
  };

  const handleReverseFinanceInvoicePayment = async (payload) => {
    const targetInvoiceId = existingInvoice?.id || resolvedInvoiceId;
    if (!targetInvoiceId || !payload?.transaction_id) {
      Swal.fire("Error", "Payment details are missing.", "error");
      return false;
    }

    try {
      setReversingFinancePayment(true);
      const response = await apiUrl.post(
        `reverseinvoicepayment/${targetInvoiceId}`,
        buildFinanceInvoiceReversePaymentPayload({
          transactionId: payload.transaction_id,
          amount: payload.amount,
          reason: payload.reason,
        }),
      );

      if (!isFinanceInvoicePaymentApiSuccess(response)) {
        Swal.fire(
          "Error",
          getFinanceInvoicePaymentApiMessage(
            response,
            "Failed to reverse payment.",
          ),
          "error",
        );
        return false;
      }

      await syncInvoiceAfterMutation(response, targetInvoiceId);
      showCommissionInvoiceSuccessToast(
        getFinanceInvoicePaymentApiMessage(
          response,
          "Payment reversed successfully.",
        ),
      );
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to reverse payment.",
        "error",
      );
      return false;
    } finally {
      setReversingFinancePayment(false);
    }
  };

  const fetchInvoiceNotes = async (
    targetInvoiceId = resolvedInvoiceId,
    { silent = false } = {},
  ) => {
    if (!targetInvoiceId) {
      setInvoiceNotes([]);
      return [];
    }

    try {
      if (!silent) {
        setLoadingInvoiceNotes(true);
      }
      const notes = normalizeInvoiceNotesList({
        data: {
          success: true,
          data: await fetchInvoiceNoteList(targetInvoiceId, {
            module: "application",
          }),
        },
      });
      setInvoiceNotes(notes);
      return notes;
    } catch (error) {
      console.error("Failed to load invoice notes", error);
      setInvoiceNotes([]);
      return [];
    } finally {
      if (!silent) {
        setLoadingInvoiceNotes(false);
      }
    }
  };

  const handleAddInvoiceNote = async (noteText) => {
    const details = String(noteText || "").trim();
    if (!details) {
      Swal.fire("Warning", "Please enter a note.", "warning");
      return false;
    }

    const invoiceRecord = existingInvoice;
    const targetInvoiceId = invoiceRecord?.id || resolvedInvoiceId;
    const invoiceNumber =
      invoiceRecord?.invoice_number ||
      invoiceRecord?.invoice_no ||
      invoiceRecord?.invoiceNo;

    if (!targetInvoiceId || !invoiceNumber) {
      Swal.fire("Error", "Invoice details are missing.", "error");
      return false;
    }

    try {
      setAddingInvoiceNote(true);
      const response = await apiUrl.post("addnoteforinvoice", {
        invoice_id: targetInvoiceId,
        invoice_number: invoiceNumber,
        details,
      });

      if (response?.data?.success || response?.data?.status) {
        await fetchInvoiceNotes(targetInvoiceId);
        return true;
      }

      Swal.fire(
        "Error",
        response?.data?.error ||
          response?.data?.message ||
          "Failed to add note.",
        "error",
      );
      return false;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to add note.",
        "error",
      );
      return false;
    } finally {
      setAddingInvoiceNote(false);
    }
  };

  const handleUpdate = async ({ id, payload }, options = {}) => {
    if (!id) {
      Swal.fire("Error", "Invoice could not be updated.", "error");
      return false;
    }

    setSaving(true);
    try {
      const isApprove = Boolean(options?.approve);
      const payments = mapCommissionPaymentsFromPayload(
        payload,
        payload?.line_items || [],
      );

      let requestPayload = mapFinanceInvoicePayloadTaxTypesForApi({ ...payload });
      if (payments.length > 0) {
        requestPayload.payments = payments;
        if (payments.length === 1) {
          requestPayload.payment = payments[0];
        }
      }

      if (isApprove) {
        requestPayload = applyCommissionApproveFields(requestPayload, {
          totalAmount: payload?.total_amount,
          paidAmount: sumCommissionPayloadPaidAmount(payload, payments),
        });
      }

      const { body, isFormData } = buildInvoiceCreateRequestBody(requestPayload);
      const response = await apiUrl.post(`editfinanceinvoice/${id}`, body, {
        ...(isFormData ? buildMultipartFormDataRequestConfig() : {}),
      });

      if (!isFinanceInvoiceApiSuccess(response)) {
        throw new Error(
          getFinanceInvoiceApiMessage(response, "Unable to update invoice."),
        );
      }

      if (isApprove) {
        showCommissionInvoiceSuccessToast("Invoice approved.");
        await syncInvoiceAfterMutation(response, id);
        if (options?.saveAndAnother) {
          return { success: true, saveAndAnother: true, stayOnPage: true };
        }
        return { success: true, stayOnPage: true };
      }

      const currentStatus = normalizeInvoiceStatus(existingInvoice?.status);
      const requestedStatus = normalizeInvoiceStatus(
        requestPayload.status || payload?.status || "draft",
      );
      const isDraftLike =
        currentStatus === "draft" || currentStatus === "awaiting_approval";
      const isApproveAction = isDraftLike && isApprove;
      const isAwaitingPaymentUpdate =
        currentStatus === "awaiting_payment" &&
        requestedStatus === "awaiting_payment";
      const isPaidOrPartialUpdate =
        (currentStatus === "paid" || currentStatus === "partial") &&
        (requestedStatus === "paid" || requestedStatus === "partial");

      if (isDraftLike) {
        showCommissionInvoiceSuccessToast(
          isApproveAction
            ? "Invoice approved."
            : `Invoice saved as ${getInvoiceStatusLabel(requestedStatus)}.`,
        );

        if (options?.saveAndAnother) {
          await refreshInvoice(id);
          return { success: true, saveAndAnother: true, stayOnPage: true };
        }

        if (isApproveAction) {
          await refreshInvoice(id);
          return { success: true, stayOnPage: true };
        }

        return { success: true, stayOnPage: true };
      }

      if (isAwaitingPaymentUpdate || isPaidOrPartialUpdate) {
        showCommissionInvoiceSuccessToast("Invoice updated.");
        await syncInvoiceAfterMutation(response, id);
        return { success: true, stayOnPage: true };
      }

      showCommissionInvoiceSuccessToast(
        `Invoice updated as ${getInvoiceStatusLabel(requestedStatus)}.`,
      );
      navigateToFinanceInvoiceList(financeListReturnTabs.statusTab);
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to update invoice.",
        "error",
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (!canViewApplicationInvoices()) {
    return (
      <div className="client-invoices-xero client-create-invoice-page p-4">
        <p className="text-muted mb-0">
          You do not have permission to view application invoices.
        </p>
      </div>
    );
  }

  if (!clientId || !resolvedInvoiceId) {
    return (
      <div className="client-invoices-xero client-create-invoice-page">
        <p className="text-muted">Invoice not found.</p>
      </div>
    );
  }

  const invoiceNumber =
    existingInvoice?.invoice_number ||
    existingInvoice?.invoice_no ||
    resolvedInvoiceId;

  return (
    <div className="client-invoices-xero client-create-invoice-page">
      <div className="client-invoices-xero__breadcrumb">
        <span>Manage Finance</span>
        <span aria-hidden="true">›</span>
        <Link to={buildFinanceInvoicesReturnUrl(financeListReturnTabs)}>
          Invoices
        </Link>
        <span aria-hidden="true">›</span>
        <span>Edit invoice {invoiceNumber}</span>
      </div>

      {loading ? (
        <InvoiceCreateWizard
          show
          pageMode
          pageLoading
          editMode
          onClose={handleClose}
        />
      ) : (
        <InvoiceCreateWizard
          key={`${resolvedInvoiceId}-${commissionShellStatus}`}
          show
          pageMode
          pageLoading={loading}
          commissionShellStatus={commissionShellStatus}
          editMode
          onClose={handleClose}
          onUpdate={handleUpdate}
          onSaveComplete={
            isDraftLikeFinanceInvoice ? handleSaveComplete : undefined
          }
          onAfterSaveAndAnother={
            isDraftLikeFinanceInvoice ? handleAfterSaveAndAnother : undefined
          }
          onInvoiceEmailSent={() =>
            fetchInvoiceNotes(resolvedInvoiceId, { silent: true })
          }
          existingInvoice={
            existingInvoice ||
            normalizeFinanceInvoiceFromRecord({ id: resolvedInvoiceId })
          }
          clientData={clientData}
          invoiceSetting={invoiceSetting}
          invoiceCatalog={invoiceCatalog}
          onCatalogLoaded={handleCatalogLoaded}
          saving={saving}
          discountFeeRules={discountFeeRules}
          existingInvoicesForNumbering={existingInvoices}
          clientApplications={clientApplications}
          loadingInvoice={loading}
          invoiceDataLoadedFromPage
          onAddInvoiceNote={handleAddInvoiceNote}
          addingInvoiceNote={addingInvoiceNote}
          invoiceNotes={invoiceNotes}
          loadingInvoiceNotes={loadingInvoiceNotes}
          onHistoryVisible={() => fetchInvoiceNotes(resolvedInvoiceId)}
          onSaveFinanceInvoicePayment={
            isDraftLikeFinanceInvoice ? null : handleAddFinanceInvoicePayment
          }
          onSaveFinanceInvoiceBulkPayment={
            isDraftLikeFinanceInvoice ? null : handleAddFinanceInvoiceBulkPayment
          }
          savingFinanceInvoicePayment={savingFinancePayment}
          onReverseFinanceInvoicePayment={
            existingInvoice?.approved ? null : handleReverseFinanceInvoicePayment
          }
          reversingFinanceInvoicePayment={reversingFinancePayment}
          onVoidInvoice={
            isDraftLikeFinanceInvoice ? undefined : handleVoidInvoice
          }
          voidingInvoice={voidingInvoice}
          onMoveInvoiceToDraft={
            isDraftLikeFinanceInvoice ? undefined : handleMoveInvoiceToDraft
          }
          movingInvoiceToDraft={movingInvoiceToDraft}
          invoiceFollowupConfig={invoiceFollowupConfig}
          onBillToClientSnapshotUpdate={(client_snapshot) => {
            setExistingInvoice((prev) =>
              prev ? { ...prev, client_snapshot } : prev,
            );
          }}
        />
      )}
    </div>
  );
}

export default FinanceEditInvoice;
